<script>
import appConfig from "@/app.config";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import Swal from "sweetalert2";
import vue2Dropzone from "vue2-dropzone";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import GoogleMapSimple from "@/components/widgets/GoogleMapSimple.vue";
import { registrar, ACCIONES } from "@/services/bitacora";
import { empleadoComputed } from "@/state/helpers";

export default {
  page: {
    title: "Negocios",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    'vue-dropzone': vue2Dropzone,
    GoogleMapSimple,
  },
  data() {
    return {
      title: "Negocios",
      items: [
        { text: "Negocios", href: "/" },
        { text: "General", active: true },
      ],
      dropzoneOptions: {
        url: "https://httpbin.org/post",
        thumbnailWidth: 150,
        maxFilesize: 3.5,
        headers: { "My-Awesome-Header": "header value" },
        acceptedFiles: "image/*",
        autoProcessQueue: false,
      },
      agregarEmpresa: false,
      editarEmpresa: false,
      empresaSelected: null,
      horario: "",
      nombre: "",
      promocion: "",
      coordenadas: "",
      tags: "",
      direccion: "",
      giro: "",
      contacto: "",
      nombreEmpresario: "",
      img: null,
      imgPreviewUrl: null, // <-- 1. DATO PARA LA VISTA PREVIA
      spotify: "",
      facebook: "",
      instagram: "",
      dataEmpresa: [],
      searchQuery: "",
      filterGiro: "todas",
      filterTag: "todas",
      giroOptions: [{ value: "todas", text: "Todos los giros" }],
      tagOptions: [{ value: "todas", text: "Todos los tags" }],
      subiendoImagen: false,
      errorImagen: null,
      currentPage: 1,
      perPage: 10,
    };
  },
  computed: {
    ...empleadoComputed,
    filteredEmpresas() {
      let filtered = this.dataEmpresa;

      if (this.filterGiro !== "todas") {
        filtered = filtered.filter((e) => e.giro === this.filterGiro);
      }
      if (this.filterTag !== "todas") {
        filtered = filtered.filter((e) =>
          (e.tags || "").toLowerCase().split(",").map(t => t.trim()).includes(this.filterTag.toLowerCase())
        );
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (e) =>
            (e.nombre && e.nombre.toLowerCase().includes(q)) ||
            (e.tags && e.tags.toLowerCase().includes(q)) ||
            (e.giro && e.giro.toLowerCase().includes(q)) ||
            (e.rfc && e.rfc.toLowerCase().includes(q))
        );
      }

      // Ordenar por register_date: más reciente primero (descendente)
      return filtered.sort((a, b) => {
        // Obtener fechas en diferentes formatos posibles
        let dateA = null;
        let dateB = null;
        
        if (a.register_date) {
          // Intentar parsear en formato DD-MM-YYYY
          dateA = moment(a.register_date, "DD-MM-YYYY", true);
          if (!dateA.isValid()) {
            // Si no funciona, intentar parsear como fecha estándar
            dateA = moment(a.register_date);
          }
        }
        
        if (b.register_date) {
          // Intentar parsear en formato DD-MM-YYYY
          dateB = moment(b.register_date, "DD-MM-YYYY", true);
          if (!dateB.isValid()) {
            // Si no funciona, intentar parsear como fecha estándar
            dateB = moment(b.register_date);
          }
        }

        // Si ambas fechas son inválidas, mantener orden original
        if ((!dateA || !dateA.isValid()) && (!dateB || !dateB.isValid())) {
          return 0;
        }
        
        // Si solo dateA es inválida, ponerla al final
        if (!dateA || !dateA.isValid()) {
          return 1;
        }
        
        // Si solo dateB es inválida, ponerla al final
        if (!dateB || !dateB.isValid()) {
          return -1;
        }

        // Ordenar de más reciente (mayor timestamp) a más antigua (menor timestamp)
        // dateB - dateA = descendente (más reciente primero)
        const timestampB = dateB.valueOf();
        const timestampA = dateA.valueOf();
        
        return timestampB - timestampA;
      });
    },
    paginatedEmpresas() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredEmpresas.slice(start, end);
    },
  },
  methods: {
    closeAndResetModal() {
      this.agregarEmpresa = false;
      this.imgPreviewUrl = null;
      this.img = null;
      this.spotify = "";
      this.facebook = "";
      this.instagram = "";
      this.nombreEmpresario = "";
      if (this.$refs.myVueDropzone) {
        this.$refs.myVueDropzone.removeAllFiles();
      }
    },

    validarCampos(empresa) {
      const camposRequeridos = ['nombre', 'contacto', 'direccion', 'promocion', 'horario', 'giro', 'tags', 'img'];
      for (const campo of camposRequeridos) {
        if (!empresa[campo]) {
          return false;
        }
      }
      return true;
    },

    GuardarEdicionEmpresa() {
      if (!this.validarCampos(this.empresaSelected)) {
        Swal.fire("Campos incompletos", "Por favor, llena todos los campos, incluyendo el logo.", "warning");
        return;
      }

      let json = {
        contacto: this.empresaSelected.contacto,
        coordenadas: this.empresaSelected.coordenadas,
        direccion: this.empresaSelected.direccion,
        giro: this.empresaSelected.giro,
        rfc: this.empresaSelected.rfc || "",
        horario: this.empresaSelected.horario,
        nombre: this.empresaSelected.nombre,
        nombreEmpresario: this.empresaSelected.nombreEmpresario || "",
        promocion: this.empresaSelected.promocion,
        tags: this.empresaSelected.tags,
        spotify: this.empresaSelected.spotify || "",
        facebook: this.empresaSelected.facebook || "",
        instagram: this.empresaSelected.instagram || "",
        img: this.empresaSelected.img,
      };

      Swal.fire({
        title: "¿Estas seguro de editar esta empresa?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Si, editar",
      }).then((result) => {
        if (result.value) {
          const antes = this.dataEmpresa.find((e) => e.id === this.empresaSelected.id);
          firebase
            .firestore()
            .collection("negocios")
            .doc(this.empresaSelected.id)
            .update(json)
            .then(() => registrar({
              accion: ACCIONES.EDITAR,
              coleccion: "negocios",
              docId: this.empresaSelected.id,
              descripcion: json.nombre,
              antes,
              despues: json,
            }))
            .then(() => {
              Swal.fire("Actualizado", "La empresa ha sido actualizada con exito", "success")
                .then(() => location.reload());
            })
            .catch((error) => {
              console.error("Error updating document: ", error);
              Swal.fire("Error", "No se pudo actualizar la empresa.", "error");
            });
        }
      });
    },

    GuardarEmpresa() {
      const nuevaEmpresa = {
        nombre: this.nombre,
        promocion: this.promocion,
        horario: this.horario,
        coordenadas: this.coordenadas,
        tags: this.tags,
        direccion: this.direccion,
        giro: this.giro,
        contacto: this.contacto,
        nombreEmpresario: this.nombreEmpresario || "",
        img: this.img, // this.img ya tiene la URL de Firebase
        spotify: this.spotify || "",
        facebook: this.facebook || "",
        instagram: this.instagram || "",
      };

      if (!this.validarCampos(nuevaEmpresa)) {
        Swal.fire("Campos incompletos", "Por favor, llena todos los campos, incluyendo el logo.", "warning");
        return;
      }

      if (this.subiendoImagen) {
        Swal.fire("Espera", "La imagen aún se está subiendo. Por favor espera.", "info");
        return;
      }

      const datosParaGuardar = {
        ...nuevaEmpresa,
        active: true,
        register_date: moment().format("DD-MM-YYYY"),
        register_by: firebase.auth().currentUser.email,
      };

      firebase.firestore().collection("negocios").add(datosParaGuardar)
        .then((ref) => registrar({
          accion: ACCIONES.CREAR,
          coleccion: "negocios",
          docId: ref.id,
          descripcion: datosParaGuardar.nombre,
          despues: datosParaGuardar,
        }))
        .then(() => {
          Swal.fire("¡Éxito!", "Tu registro ha sido guardado.", "success")
            .then(() => location.href = "/empresas/index/");
        })
        .catch((error) => {
          Swal.fire("Error", `Ocurrió un error al guardar: ${error.message}`, "error");
        });
    },

    openEdit(data) {
      this.empresaSelected = { ...data };
      this.editarEmpresa = true;
    },

    deleteEmpresa(id) {
      Swal.fire({
        title: "¿Estas seguro de eliminar esta empresa?",
        text: "Esta empresa se eliminará permanentemente.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Borrar",
      }).then((result) => {
        if (result.value) {
          const antes = this.dataEmpresa.find((e) => e.id === id);
          firebase.firestore().collection("negocios").doc(id).delete()
            .then(() => registrar({
              accion: ACCIONES.ELIMINAR,
              coleccion: "negocios",
              docId: id,
              descripcion: antes ? antes.nombre : id,
              antes,
            }))
            .then(() => {
              Swal.fire("Eliminado", "Esta empresa ha sido eliminada con exito", "success")
                .then(() => location.reload());
            })
            .catch((error) => Swal.fire("Error", `No se pudo eliminar: ${error.message}`, "error"));
        }
      });
    },

    // <-- 2. MÉTODO MODIFICADO PARA CREAR VISTA PREVIA
    previewFile(file) {
      // Limpiar archivo anterior de dropzone
      if (this.editarEmpresa && this.$refs.myVueDropzoneEditar) {
        this.$refs.myVueDropzoneEditar.removeFile(file);
      } else if (this.$refs.myVueDropzone) {
        this.$refs.myVueDropzone.removeFile(file);
      }

      this.subiendoImagen = true;
      this.errorImagen = null;
      this.imgPreviewUrl = null; // Limpiar vista previa anterior

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result;
        // Asignar a la URL de vista previa para mostrarla al instante
        if (!this.editarEmpresa) {
          this.imgPreviewUrl = base64;
        }

        const storageRef = firebase.storage().ref(`negocios/${uuidv4()}.png`);
        storageRef.putString(base64.split(',')[1], 'base64', { contentType: file.type })
          .then(snapshot => snapshot.ref.getDownloadURL())
          .then(url => {
            if (this.editarEmpresa && this.empresaSelected) {
              this.empresaSelected.img = url;
            } else {
              this.img = url; // Asigna la URL final de Firebase
            }
            this.subiendoImagen = false;
          })
          .catch(error => {
            this.subiendoImagen = false;
            this.errorImagen = "Error al subir la imagen. Intenta de nuevo.";
            console.error("Error al subir imagen:", error);
          });
      };
    },

    descargarCSV() {
      // ... (código sin cambios)
    }
  },
  mounted() {
    firebase.firestore().collection("negocios").get()
      .then((querySnapshot) => {
        const array = [];
        const giros = new Set();
        const tagsSet = new Set();
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const json = {
            id: doc.id,
            ...data,
            nombreEmpresario: data.nombreEmpresario || '',
            spotify: data.spotify || '',
            facebook: data.facebook || '',
            instagram: data.instagram || '',
          };
          array.push(json);
          if (data.giro) giros.add(data.giro);
          if (data.tags) {
            data.tags.split(",").forEach(tag => tagsSet.add(tag.trim()));
          }
        });
        // Ordenar el array por register_date antes de asignarlo
        array.sort((a, b) => {
          let dateA = null;
          let dateB = null;
          
          if (a.register_date) {
            dateA = moment(a.register_date, "DD-MM-YYYY", true);
            if (!dateA.isValid()) {
              dateA = moment(a.register_date);
            }
          }
          
          if (b.register_date) {
            dateB = moment(b.register_date, "DD-MM-YYYY", true);
            if (!dateB.isValid()) {
              dateB = moment(b.register_date);
            }
          }

          if ((!dateA || !dateA.isValid()) && (!dateB || !dateB.isValid())) {
            return 0;
          }
          if (!dateA || !dateA.isValid()) {
            return 1;
          }
          if (!dateB || !dateB.isValid()) {
            return -1;
          }

          return dateB.valueOf() - dateA.valueOf();
        });
        
        this.dataEmpresa = array;
        this.currentPage = 1; // Resetear a la primera página cuando se cargan los datos
        this.giroOptions = [{ value: "todas", text: "Todos los giros" }, ...Array.from(giros).map(g => ({ value: g, text: g }))];
        this.tagOptions = [{ value: "todas", text: "Todos los tags" }, ...Array.from(tagsSet).map(t => ({ value: t, text: t }))];
      });
  },
};
</script>

<template>
  <div class="row">
    <div class="col-lg-12">
      <div class="card">
        <div class="card-body">
          <div class="row d-flex justify-content-between align-items-center">
            <div class="col-md-9">
              <b-form-group label="Buscador por nombre, giro o tag:" label-for="searchInput" class="mb-0">
                <b-form-input id="searchInput" v-model="searchQuery" placeholder="Buscar..." />
              </b-form-group>
            </div>
            <div class="col-md-3 d-flex align-items-end">
              <b-form-group label="Nuevo negocio o descargar csv:" label-for="descargarCSV" class="mb-0">
                <b-button variant="info" @click="descargarCSV">
                  <i class="mdi mdi-download"></i>
                </b-button>
                <b-button class="ml-3" variant="success" @click="agregarEmpresa = true">
                  Nuevo Negocio
                </b-button>
              </b-form-group>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-xl-12">
      <div class="card">
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-centered mb-0 table-hover table-nowrap mt-2">
              <thead class="thead-light">
                <tr>
                  <th>Logo</th>
                  <th>Nombre</th>
                  <th>RFC</th>
                  <th>Maps</th>
                  <th>Ver más</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="data in paginatedEmpresas" :key="data.id">
                  <td><img width="30" :src="data.img" /></td>
                  <td>{{ data.nombre }}</td>
                  <td>{{ data.rfc || '—' }}</td>
                  <td>{{ data.coordenadas }}</td>
                  <td>
                    <button type="button" class="btn btn-success" @click="openEdit(data)">
                      <i class="mdi mdi-square-edit-outline ms-2"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="filteredEmpresas.length === 0">
                  <td colspan="5" class="text-center text-muted py-4">
                    No hay empresas que coincidan con tu búsqueda.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="d-flex justify-content-center my-3">
          <b-pagination v-model="currentPage" :total-rows="filteredEmpresas.length" :per-page="perPage"
            aria-controls="tabla-empresas" align="center" size="md" pills></b-pagination>
        </div>
      </div>
    </div>

    <b-modal v-model="agregarEmpresa" id="add" size="lg" title="Nueva Empresa" centered hide-footer>
      <form @submit.prevent="GuardarEmpresa">
        <div class="col-sm-12">
          <div class="form-group">
            <div class="row">
              <div class="col-sm-6">
                <label class="control-label">Nombre de la empresa</label>
                <input v-model.trim="nombre" type="text" class="form-control" required />
              </div>
              <div class="col-sm-6">
                <label class="control-label">Contacto</label>
                <input v-model.trim="contacto" type="text" class="form-control" required />
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-sm-12">
                <label class="control-label">Nombre del Empresario</label>
                <input v-model.trim="nombreEmpresario" type="text" class="form-control" />
              </div>
            </div>
          </div>
          <div class="form-group">
            <label class="control-label">Dirección</label>
            <GoogleMapSimple v-model="coordenadas" :address-value.sync="direccion" />
            <input type="text" :value="direccion" class="form-control" style="display:none;" required>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-sm-6">
                <label class="control-label">Promoción</label>
                <input v-model.trim="promocion" type="text" class="form-control" required />
              </div>
              <div class="col-sm-6">
                <label class="control-label">Horario</label>
                <input v-model.trim="horario" type="text" class="form-control" required />
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-sm-12">
                <label class="control-label">Giro de la empresa</label>
                <input v-model.trim="giro" type="text" class="form-control" required />
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-sm-12">
                <label class="control-label">Tags (separados por coma)</label>
                <input v-model.trim="tags" type="text" class="form-control" required />
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-sm-4">
                <label class="control-label">Spotify</label>
                <input v-model.trim="spotify" type="text" class="form-control" placeholder="URL de Spotify" />
              </div>
              <div class="col-sm-4">
                <label class="control-label">Facebook</label>
                <input v-model.trim="facebook" type="text" class="form-control" placeholder="URL de Facebook" />
              </div>
              <div class="col-sm-4">
                <label class="control-label">Instagram</label>
                <input v-model.trim="instagram" type="text" class="form-control" placeholder="URL de Instagram" />
              </div>
            </div>
          </div>
          <div class="form-group">
            <label class="control-label">Logo</label>
            <vue-dropzone id="dropzone" ref="myVueDropzone" :use-custom-slot="true" :options="dropzoneOptions"
              @vdropzone-file-added="previewFile"></vue-dropzone>

            <div v-if="imgPreviewUrl" class="mt-3 text-center">
              <p>Vista Previa:</p>
              <img :src="imgPreviewUrl" class="img-thumbnail" style="max-width: 200px;" alt="Vista previa del logo" />
            </div>

          </div>
        </div>
        <div class="modal-footer">
          <button type="submit" class="btn btn-success" :disabled="subiendoImagen">Guardar</button>
          <b-button variant="secondary" @click="closeAndResetModal">Cerrar</b-button>
        </div>
      </form>
    </b-modal>

    <b-modal v-model="editarEmpresa" id="edit" size="lg" title="Editar Empresa" centered hide-footer>
      <form @submit.prevent="GuardarEdicionEmpresa" v-if="empresaSelected">
        <div class="col-sm-12">
          <div class="form-group">
            <div class="row">
              <div class="col-sm-6">
                <label class="control-label">Nombre de la empresa</label>
                <input v-model.trim="empresaSelected.nombre" type="text" class="form-control" required />
              </div>
              <div class="col-sm-6">
                <label class="control-label">Contacto</label>
                <input v-model.trim="empresaSelected.contacto" type="text" class="form-control" required />
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-sm-12">
                <label class="control-label">Nombre del Empresario</label>
                <input v-model.trim="empresaSelected.nombreEmpresario" type="text" class="form-control" />
              </div>
            </div>
          </div>
          <div class="form-group">
            <label class="control-label">Dirección</label>
            <GoogleMapSimple v-model="empresaSelected.coordenadas" :address-value.sync="empresaSelected.direccion" />
            <input type="text" :value="empresaSelected.direccion" class="form-control" style="display:none;" required>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-sm-6">
                <label class="control-label">Promoción</label>
                <input v-model.trim="empresaSelected.promocion" type="text" class="form-control" required />
              </div>
              <div class="col-sm-6">
                <label class="control-label">Horario</label>
                <input v-model.trim="empresaSelected.horario" type="text" class="form-control" required />
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-sm-6">
                <label class="control-label">Giro de la empresa</label>
                <input v-model.trim="empresaSelected.giro" type="text" class="form-control" required />
              </div>
              <div class="col-sm-6">
                <label class="control-label">RFC</label>
                <input v-model.trim="empresaSelected.rfc" type="text" class="form-control" placeholder="RFC del negocio" />
              </div>
            </div>
          </div>
          <div class="form-group">
            <label class="control-label">Tags (separados por coma)</label>
            <input v-model.trim="empresaSelected.tags" type="text" class="form-control" required />
          </div>
          <div class="form-group">
            <div class="row">
              <div class="col-sm-4">
                <label class="control-label">Spotify</label>
                <input v-model.trim="empresaSelected.spotify" type="text" class="form-control" placeholder="URL de Spotify" />
              </div>
              <div class="col-sm-4">
                <label class="control-label">Facebook</label>
                <input v-model.trim="empresaSelected.facebook" type="text" class="form-control" placeholder="URL de Facebook" />
              </div>
              <div class="col-sm-4">
                <label class="control-label">Instagram</label>
                <input v-model.trim="empresaSelected.instagram" type="text" class="form-control" placeholder="URL de Instagram" />
              </div>
            </div>
          </div>
          <div class="form-group">
            <b-row>
              <b-col md="6">
                <b-form-group label="Subir nuevo logo">
                  <vue-dropzone id="dropzone-editar" ref="myVueDropzoneEditar" :use-custom-slot="true"
                    :options="{ ...dropzoneOptions, maxFiles: 1 }" @vdropzone-file-added="previewFile"></vue-dropzone>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Logo actual">
                  <div v-if="empresaSelected.img" class="mt-2 text-center">
                    <img :src="empresaSelected.img" alt="Logo actual" class="img-thumbnail" style="max-width: 260px;" />
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </div>
        <div class="modal-footer">
          <button type="submit" class="btn btn-success" :disabled="subiendoImagen">Guardar Cambios</button>
          <b-button v-if="puedeEditar" variant="danger"
            @click="deleteEmpresa(empresaSelected.id)">Eliminar</b-button>
          <b-button variant="secondary" @click="editarEmpresa = false">Cerrar</b-button>
        </div>
      </form>
    </b-modal>
  </div>
</template>